import { useEffect } from "react";
import Navbar from "@/components/clinic/Navbar";
import Hero from "@/components/clinic/Hero";
import Approach from "@/components/clinic/Approach";
import Gallery from "@/components/clinic/Gallery";
import ResultsGallery from "@/components/clinic/ResultsGallery";
import Offer from "@/components/clinic/Offer";
import FAQ from "@/components/clinic/FAQ";
import Booking from "@/components/clinic/Booking";
import LocationSEO from "@/components/clinic/LocationSEO";
import Footer from "@/components/clinic/Footer";
import { applyJsonLd, applySeo, SITE_URL } from "@/lib/seo";

const Index = () => {
  useEffect(() => {
    applySeo({
      title: "Blepharoplasty London — Eyelid Surgery | Dr Vahe Clinic",
      description:
        "Specialist blepharoplasty in London with Dr Vahe. Upper eyelid surgery from £2,300 and upper and lower eyelid surgery from £3,100.",
      path: "/",
    });

    // Site-wide LocalBusiness/MedicalOrganization/Service/FAQ schema is
    // injected globally by <SiteSchema /> in App.tsx.
    //
    // The page-scoped @graph below adds three nodes that strengthen the
    // homepage's rich-result eligibility:
    //
    //   1. Organization  — top-level brand entity, distinct from the
    //      LocalBusiness node, so Google can merge the two via sameAs.
    //   2. MedicalWebPage — describes THIS page (about, audience,
    //      lastReviewed, specialty, mainEntity).
    //   3. BreadcrumbList — keeps the existing breadcrumb signal.
    const lastReviewed = new Date().toISOString().slice(0, 10);
    const ogImage =
      "https://storage.googleapis.com/gpt-engineer-file-uploads/IBo7X3t19XTGsumpVFkqpYNqnx23/social-images/social-1777281190200-Blepharolplasty.webp";

    const cleanup = applyJsonLd("homepage-graph", {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": `${SITE_URL}/#organization`,
          name: "Dr Vahe Clinic",
          url: SITE_URL,
          logo: {
            "@type": "ImageObject",
            url: `${SITE_URL}/favicon.ico`,
            caption: "Dr Vahe Clinic",
          },
          // Same-as the LocalBusiness/MedicalOrganization defined in
          // SiteSchema so Google can fold both into one entity.
          sameAs: [SITE_URL],
          subOrganization: { "@id": `${SITE_URL}/#clinic` },
        },
        {
          "@type": "MedicalWebPage",
          "@id": `${SITE_URL}/#webpage`,
          url: `${SITE_URL}/`,
          name: "Blepharoplasty London — Eyelid Surgery | Dr Vahe Clinic",
          description:
            "Specialist blepharoplasty in London with Dr Vahe. Upper eyelid surgery from £2,300 and upper and lower eyelid surgery from £3,100.",
          inLanguage: "en-GB",
          isPartOf: { "@id": `${SITE_URL}/#website` },
          about: { "@id": `${SITE_URL}/#service-upper-blepharoplasty` },
          mainEntity: { "@id": `${SITE_URL}/#service-upper-lower-blepharoplasty` },
          primaryImageOfPage: {
            "@type": "ImageObject",
            url: ogImage,
          },
          lastReviewed,
          reviewedBy: { "@id": `${SITE_URL}/#dr-vahe` },
          audience: {
            "@type": "MedicalAudience",
            audienceType: "Patient",
            geographicArea: {
              "@type": "AdministrativeArea",
              name: "Greater London",
            },
          },
          specialty: "https://schema.org/PlasticSurgery",
          medicalAudience: ["Patient"],
          significantLink: [`${SITE_URL}/privacy`, `${SITE_URL}/terms`],
          breadcrumb: { "@id": `${SITE_URL}/#breadcrumb-home` },
          publisher: { "@id": `${SITE_URL}/#clinic` },
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${SITE_URL}/#breadcrumb-home`,
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          ],
        },
      ],
    });

    return () => cleanup();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <Hero />
        <Approach />
        <Gallery />
        <ResultsGallery />
        <Offer />
        <FAQ />
        <Booking />
        <LocationSEO />
      </main>
      <Footer />
    </div>
  );
};

export default Index;
